import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  TextField,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Description as FormIcon,
  Notifications as ReminderIcon,
  Event as AppointmentIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useAccessToken, useSubjectLanguage, useUpdateOnboardingStep } from '@features/user';
import APIService from '../../services/APIService';
import { useAppDispatch } from '@store/hooks';
import { popModal, pushModal } from '@store/slices/modalSlice';
import { buildOnboardingSmsPreview } from '../patient-communication/constants';
import type { ModalComponentProps } from './constants';
import { useSessionReplayCapture, useEventTracking } from '@services/statsig';

interface PatientCommunicationOnboardingProps extends ModalComponentProps {}

interface StepContent {
  title: string;
  content: React.ReactNode;
  verification?: () => Promise<boolean> | boolean;
  isCompleted?: () => boolean;
}

const PatientCommunicationOnboarding: React.FC<PatientCommunicationOnboardingProps> = ({
  modalProps,
  closeModal,
}) => {
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [communicationsName, setCommunicationsName] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const updateOnboardingStep = useUpdateOnboardingStep();
  const { startCapture } = useSessionReplayCapture();
  const { smsOnboarding } = useEventTracking();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Start session replay and fetch communications name on mount
  useEffect(() => {
    const fetchCommunicationsName = async () => {
      setChecking(true);
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/user/getProviderBillingInfo`,
          accessToken,
        });

        if (response.ok) {
          const data = response.value.provider_billing_info || {};

          // Initialize communications name from provider info if available
          if (data.communications_name) {
            setCommunicationsName(data.communications_name);
          }
        }
      } catch (error) {
        console.error('Error fetching provider information:', error);
      } finally {
        setChecking(false);
      }
    };

    // Start session replay capture when the component mounts
    startCapture('patient_communication_onboarding');

    fetchCommunicationsName();
  }, []);

  const saveCommunicationsName = useCallback(async () => {
    // Only allow saving if communications name is not empty
    if (!communicationsName.trim()) {
      return false;
    }

    setSaving(true);
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/user/updateProviderBillingInfo',
        accessToken,
        body: {
          updated_fields: { communications_name: communicationsName },
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error saving communications name:', error);
      return false;
    } finally {
      setSaving(false);
    }
  }, [communicationsName, getAccessToken]);

  // Debounced save function
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveCommunicationsName();
    }, 2000); // 2 seconds delay
  }, [saveCommunicationsName]);

  // Handle communications name change
  const handleCommunicationsNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommunicationsName(e.target.value);
    debouncedSave();
  };

  // Handle field blur - save immediately unless clicking a button (which will handle save via verification)
  const handleCommunicationsNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Check if the blur is caused by clicking a button (Next/Back)
    // If so, don't trigger a save here - handleNext will handle it via verification
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget?.tagName === 'BUTTON') {
      return;
    }

    saveCommunicationsName();
  };

  // Mock function to register onboarding completion
  const registerOnboardingComplete = async () => {
    setChecking(true);
    try {
      console.log('Onboarding completion registered successfully');

      await updateOnboardingStep('patient_communication_sms_onboarding_completed', true);

      // Track SMS onboarding completion
      smsOnboarding.completed({});

      return true;
    } catch (error) {
      console.error('Error registering onboarding completion:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  // SMS preview component with support for different message types
  const SMSPreview = ({
    displayName,
    messageType = 'form',
  }: {
    displayName: string;
    messageType?: 'form' | 'reminder' | 'appointment';
  }) => {
    const message = buildOnboardingSmsPreview(displayName || 'Your Provider', messageType);

    return (
      <Box sx={{ my: 2 }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 280,
            p: 2,
            borderRadius: '20px 20px 5px 20px',
            bgcolor: '#34C759', // iOS message green
            color: 'white',
            position: 'relative',
            '&:before': {
              content: "''",
              position: 'absolute',
              bottom: 0,
              left: -10,
              width: 20,
              height: 20,
              borderRadius: '0 0 20px 0',
              boxShadow: '5px 0 0 0 #34C759',
              bgcolor: 'transparent',
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 400,
            }}
          >
            {message}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Helper function to capitalize first letter
  function capitalize(string: string | undefined): string {
    if (!string) return 'Patient';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // Communication features overview component
  const CommunicationFeatures = () => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FormIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Forms / Questionnaires
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Collect {subjectLanguage || 'patient'} information before appointments
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ReminderIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Reminders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send automated appointment reminders
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AppointmentIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Appointment Confirmations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {capitalize(subjectLanguage || 'patient')}s can confirm their appointments with a simple
            reply
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Collect payment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Charge a card on file, send a payment request, or log money already received
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Step content definitions - condensed to 3 steps
  const steps: StepContent[] = [
    // Welcome step - combined with overview
    {
      title: `${capitalize(subjectLanguage || 'Patient')} Communication Setup`,
      content: (
        <Box>
          <Typography sx={{ mb: 2 }} component="p">
            Set up SMS communication with your {subjectLanguage || 'patient'}s for forms, reminders,
            and appointment confirmations.
          </Typography>

          <CommunicationFeatures />

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              All communications are sent via secure SMS to ensure {subjectLanguage || 'patient'}{' '}
              privacy and HIPAA compliance.
            </Typography>
          </Alert>
        </Box>
      ),
    },

    // Provider info & personalization - simplified step
    {
      title: 'Personalize Your Messages',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Set Your Display Name
          </Typography>
          <Typography sx={{ mb: 2 }} component="p">
            This name will appear in all SMS messages sent to your {subjectLanguage || 'patient'}s.
          </Typography>
          <TextField
            fullWidth
            required
            label="Your name or practice name"
            value={communicationsName}
            onChange={handleCommunicationsNameChange}
            onBlur={handleCommunicationsNameBlur}
            variant="outlined"
            placeholder="Dr. Smith"
            sx={{ my: 2 }}
            helperText={saving ? 'Saving...' : communicationsName ? 'Saved automatically' : ''}
          />
          <Typography sx={{ mb: 2 }} component="p">
            Your {subjectLanguage || 'patient'}s will see your name in SMS messages:
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <SMSPreview displayName={communicationsName} messageType="form" />
          </Box>
        </Box>
      ),
      verification: async () => {
        // Verify communications name is not empty
        if (!communicationsName.trim()) {
          return false;
        }
        const saved = await saveCommunicationsName();
        return saved;
      },
      isCompleted: () => !!communicationsName.trim(),
    },

    // Requirements & completion - final step
    {
      title: 'Ready to Communicate',
      content: (
        <Box>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Setup Complete!
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom>
            {capitalize(subjectLanguage || 'patient')} Requirements
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            To send communications to a {subjectLanguage || 'patient'}, ensure you have:
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
              <Typography>
                <strong>Phone number</strong> - Required for all SMS communications
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
              <Typography>
                <strong>Date of birth</strong> - Required for forms (identity verification)
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Available Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip icon={<FormIcon />} label="Send Form" color="primary" variant="outlined" />
            <Chip
              icon={<ReminderIcon />}
              label="Send Reminder"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<PaymentIcon />}
              label="Collect payment"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Look for these when scheduling an appointment. You can also set default appointment
            types so that JotPsych always sends forms or reminders for these types of appointments.
          </Typography>
        </Box>
      ),
    },
  ];

  const handleNext = async () => {
    const currentStep = steps[activeStep];

    console.log('currentStep', currentStep);

    // For provider information step, validate communications name
    if (activeStep === 1) {
      if (!communicationsName.trim()) {
        return; // Don't proceed if communications name is missing
      }
    }

    if (currentStep.verification) {
      setChecking(true);
      const passed = await currentStep.verification();
      setChecking(false);

      if (!passed) {
        // Show error or handle failed verification
        return;
      }
    }

    // Register onboarding complete when moving from provider info to final step
    if (activeStep === 1) {
      setChecking(true);
      const registrationSuccess = await registerOnboardingComplete();
      setChecking(false);

      if (!registrationSuccess) {
        console.error('Failed to register onboarding completion');
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep(prevStep => prevStep + 1);
    } else {
      // Final step - remove this modal from stack first, then push continuation modal if provided
      // This ensures proper ordering: pop current modal, then push new one
      dispatch(popModal());

      if (modalProps.continuationAction) {
        // Handle continuation action - it should be a pushModal action
        if (modalProps.continuationAction.type === 'modals/pushModal') {
          const { modalType, modalProps: continuationModalProps } =
            modalProps.continuationAction.payload;
          dispatch(pushModal({ modalType, modalProps: continuationModalProps }));
        } else {
          // Fallback for other action types (though unlikely in this context)
          dispatch({
            ...modalProps.continuationAction,
            type: modalProps.continuationAction.type,
            payload: modalProps.continuationAction.payload,
          });
        }
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  return (
    <>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        {capitalize(subjectLanguage || 'Patient')} Communication Setup
        <IconButton edge="end" aria-label="close" onClick={closeModal} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, pt: 2 }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {steps[activeStep].content}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || checking}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>

        <Tooltip
          title={
            activeStep === 1 && !communicationsName.trim()
              ? 'Please enter your name or practice name to continue'
              : ''
          }
          placement="top"
          arrow
        >
          <Box>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={checking || saving || (activeStep === 1 && !communicationsName.trim())}
              endIcon={
                checking || saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : activeStep === steps.length - 1 ? undefined : (
                  <ArrowForwardIcon />
                )
              }
            >
              {activeStep === steps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </>
  );
};

export default PatientCommunicationOnboarding;
